import React, { useEffect, useState } from 'react';
import { League } from './model/League';
import { Player } from './model/Player';
import { Team } from './model/Team';
import { ThemeProvider } from './components/theme-provider';
import { Header } from './components/header';
import { Leaderboard } from './components/leaderboard';
import { TopPerformers } from './components/top-performers';
import { TeamCardCompact } from './components/team-card';

const endpoint =
  'https://f3v080ez1k.execute-api.us-west-2.amazonaws.com/dev/3487f254-a1dd-484a-bf81-d11677408e9f?year=2025';

const fetchData = async () => {
  const data = await fetch(endpoint).then(res => res.json());
  return data;
};

const sumPoints = (players: Player[], mvp: Player) => {
  let totalScore = 0;
  players.forEach(player => {
    if (player.id === mvp.id) {
      totalScore += 1.5 * player.points;
    } else {
      totalScore += player.points;
    }
  })

  return totalScore;
}

const convertToModel = (json: any): League => {
  const players: { [id: string]: Player } = {};
  const teams: { [id: string]: Team } = {};
  json.players.forEach(
    (player: any) =>
      (players[player.id] = {
        name: player.name,
        id: player.id,
        points: Number.parseFloat(player.points),
        position: player.position,
        nflTeam: player.nflTeam || 'TBD', // Use API field or placeholder
      })
  );

  json.teams.forEach(
    (team: any) =>
      (teams[team.id] = {
        name: team.name,
        id: team.id,
        players: team.players.map((playerId: string) => players[playerId]),
        mvp: players[team.mvp],
        score: sumPoints(team.players.map((playerId: string) => players[playerId]), players[team.mvp])
      })
  );

  return {
    id: json.league.id as string,
    name: json.league.name as string,
    teams: Object.values(teams).sort((a, b) => a.score >= b.score ? -1 : 1),
  };
};

let init = false;

function AppContent() {
  const [league, setLeague] = useState<League | undefined>();
  const [loading, setLoading] = useState<boolean>(true);

  const refreshData = async () => {
    try {
      const data = await fetchData();
      const leagueData = convertToModel(data);
      setLeague(leagueData);
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  };

  useEffect(() => {
    if (!init) {
      refreshData().then(() => {
        setLoading(false);
      });
    }
    init = true;
  }, []);

  // Auto-refresh data every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refreshData();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header leagueName={league?.name} />

      <main className="container mx-auto px-4 py-8">
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Leaderboard and Top Performers - takes up 1 column on large screens */}
          <div className="lg:col-span-1 space-y-6">
            <Leaderboard teams={league?.teams || []} />
            <TopPerformers teams={league?.teams || []} />
          </div>

          {/* Teams - takes up 2 columns on large screens */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">Teams</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {league?.teams.map(team => (
                  <TeamCardCompact key={team.id} team={team} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 border-t pt-8 text-center text-sm text-muted-foreground">
          <div className="flex flex-col space-y-2 sm:flex-row sm:justify-center sm:space-x-4 sm:space-y-0">
            <a
              href="https://www.flaticon.com/free-icons/turkey"
              className="hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Icon by freepik
            </a>
            <a
              href="https://fantasydata.com/nfl/fantasy-football-leaders?season=2022&seasontype=1&scope=2&subscope=1&scoringsystem=7&startweek=12&endweek=12&aggregatescope=1&range=3"
              className="hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Scoring by fantasydata
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="turkey-bowl-theme">
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
